<template>
  <hs-modal v-model="open" title="Generate Join Code" size="sm" @close="$emit('close')">
    <div class="gjcm-body">
      <div v-if="!generatedCode">
        <p class="gjcm-desc">Create a join code to invite stores to join "{{ group.name }}".</p>

        <hs-select
          v-model="expiresInHours"
          label="Code Expires In"
          :options="expiresOptions"
          class="gjcm-field"
        />

        <hs-alert v-if="error" tone="danger" :message="error" class="gjcm-field" />
      </div>

      <div v-else class="gjcm-success">
        <hs-alert tone="success" title="Join code generated!" message="Share this code with stores you want to invite to the group." />

        <div class="gjcm-code-card">
          <p class="gjcm-code-label">Join Code</p>
          <div class="gjcm-code-row">
            <code class="gjcm-code">{{ generatedCode.code }}</code>
            <hs-button
              :variant="copied ? 'secondary' : 'ghost'"
              :icon="copied ? 'check' : 'copy'"
              size="sm"
              @click="copyCode"
            >
              {{ copied ? "Copied!" : "Copy" }}
            </hs-button>
          </div>
          <p class="gjcm-code-expiry">
            <vue-feather type="clock" size="12" />
            <span v-if="generatedCode.expires_at">Expires: {{ formatDate(generatedCode.expires_at) }}</span>
            <span v-else>Never expires</span>
          </p>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="gjcm-footer">
        <template v-if="!generatedCode">
          <hs-button variant="ghost" @click="$emit('close')">Cancel</hs-button>
          <hs-button variant="primary" icon="key" :loading="generating" @click="generateCode">
            Generate Code
          </hs-button>
        </template>
        <template v-else>
          <hs-button variant="primary" @click="handleDone">Done</hs-button>
        </template>
      </div>
    </template>
  </hs-modal>
</template>

<script>
import { ref, computed } from "vue";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import { HsModal, HsButton, HsSelect, HsAlert } from "@/components/ui";

export default {
  name: "GenerateJoinCodeModal",
  components: { HsModal, HsButton, HsSelect, HsAlert },
  props: {
    group: { type: Object, required: true },
  },
  emits: ["close", "generated"],
  setup(props, { emit }) {
    const open = ref(true);
    const generating = ref(false);
    const error = ref("");
    const expiresInHours = ref(168);
    const generatedCode = ref(null);
    const copied = ref(false);

    const expiresOptions = computed(() => [
      { label: "24 hours", value: 24 },
      { label: "3 days", value: 72 },
      { label: "7 days", value: 168 },
      { label: "30 days", value: 720 },
      { label: "Never expires", value: null },
    ]);

    const generateCode = async () => {
      generating.value = true;
      error.value = "";
      try {
        const payload = {};
        if (expiresInHours.value) payload.expires_in_hours = expiresInHours.value;

        const response = await http.post(`/v2/groups/${props.group.id}/join-code`, payload);
        if (!response?.data?.code) throw new Error("No join code in response");

        generatedCode.value = response.data;
        toast.success("Join code generated successfully!");
      } catch (err) {
        console.error("Failed to generate join code:", err);
        error.value = err?.response?.data?.error?.message || err?.message || "Unable to generate join code.";
      } finally {
        generating.value = false;
      }
    };

    const copyCode = async () => {
      if (!generatedCode.value?.code) return;
      try {
        await navigator.clipboard.writeText(generatedCode.value.code);
        copied.value = true;
        toast.success("Code copied to clipboard!");
        setTimeout(() => { copied.value = false; }, 2000);
      } catch (err) {
        toast.error("Failed to copy code");
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return "Never";
      return new Date(dateString).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    };

    const handleDone = () => {
      if (generatedCode.value) emit("generated", generatedCode.value);
      emit("close");
    };

    return { open, generating, error, expiresInHours, expiresOptions, generatedCode, copied, generateCode, copyCode, formatDate, handleDone };
  },
};
</script>

<style scoped>
.gjcm-body {
  display: grid;
  gap: 1rem;
}

.gjcm-desc {
  color: var(--hs-muted);
  font-size: 0.85rem;
  margin: 0 0 1rem;
}

.gjcm-field {
  margin-top: 0.75rem;
}

.gjcm-success {
  display: grid;
  gap: 1rem;
}

.gjcm-code-card {
  background: color-mix(in srgb, var(--hs-primary) 6%, transparent);
  border: 2px solid color-mix(in srgb, var(--hs-primary) 25%, transparent);
  border-radius: 14px;
  display: grid;
  gap: 0.6rem;
  padding: 1.1rem 1.25rem;
}

.gjcm-code-label {
  color: var(--hs-primary);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  margin: 0;
  text-transform: uppercase;
}

.gjcm-code-row {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
}

.gjcm-code {
  color: var(--hs-text);
  font-family: monospace;
  font-size: 2rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  user-select: all;
}

.gjcm-code-expiry {
  align-items: center;
  color: var(--hs-muted);
  display: flex;
  font-size: 0.78rem;
  gap: 0.35rem;
  margin: 0;
}

.gjcm-footer {
  display: flex;
  gap: 0.6rem;
  justify-content: flex-end;
}
</style>
